import {
	type ClinicalProgramType,
	Laterality,
	type Prisma,
	type ProgramExercise,
	type ProgramStatus,
	type ProgramType,
	RoutineType,
} from "@brookbush/database";

import z from "zod";

import type { Exercise } from "../exercises.types";
import { programInputSchema } from "./input.types";

export * from "./input.types";
export * from "./output.types";

export type ProgramExerciseProgression = Exercise & { notes: string | null };

export type ProgramExerciseExpanded = ProgramExercise &
	(
		| (Exercise & {
				isCustom: false;
				customData: null;
				exerciseId: string;
		  })
		| {
				isCustom: true;
				customData: true;
				exerciseId: null;
		  }
	) & {
		progression: ProgramExerciseProgression | null;
	};

export type FinalProgramType =
	| Extract<ProgramType, "strength_training">
	| ClinicalProgramType;

type ProgramExpandedPrisma = Prisma.ProgramGetPayload<{
	include: {
		user: true;
		duplicatedFrom: true;
		duplicates: true;
		invitation: {
			include: {
				user: true;
			};
		};

		clinicalProgram: {
			include: {
				hypothesizedChanges: true;

				mobilityRoutine: {
					include: {
						exercises: true;
					};
				};
				activationIntegrationRoutine: {
					include: {
						acuteVariables: true;
						exercises: true;
					};
				};
				homeExerciseRoutine: {
					include: {
						acuteVariables: true;
						exercises: true;
					};
				};
			};
		};

		strengthTrainingProgram: {
			include: {
				phases: {
					include: {
						acuteVariables: true;
						warmupRoutine: {
							include: {
								exercises: true;
							};
						};
						lowerWarmupRoutine: {
							include: {
								exercises: true;
							};
						};
						upperWarmupRoutine: {
							include: {
								exercises: true;
							};
						};
						mainRoutine: {
							include: {
								exercises: true;
							};
						};
						undulationRoutine: {
							include: {
								exercises: true;
							};
						};
					};
				};
			};
		};
	};
}>;

type DeepReplaceExercises<T> = T extends (infer U)[]
	? DeepReplaceExercises<U>[]
	: T extends object
		? {
				[K in keyof T]: K extends "exercises"
					? ProgramExerciseExpanded[]
					: DeepReplaceExercises<T[K]>;
			}
		: T;

export type ProgramExpanded = Omit<
	DeepReplaceExercises<ProgramExpandedPrisma>,
	"type"
> & {
	type: FinalProgramType;
	buildEtaSeconds: number;
	buildProgress: number;
	shouldShowSurvey: boolean;
};

export type ProgramRoutineExpanded = DeepReplaceExercises<
	Prisma.ProgramRoutineGetPayload<{
		include: {
			exercises: true;
		};
	}>
>;

export type ProgramPreview = Omit<
	DeepReplaceExercises<Prisma.ProgramGetPayload<{}>>,
	"type"
> & {
	type: FinalProgramType;
	buildEtaSeconds: number;
	buildProgress: number;
	/** Who created the program, set only for programs shared with the viewer */
	ownerName?: string | null;
	_type: "program";
};

/**
 * A pending invitation to someone else's program. Deliberately limited to
 * fields safe to show before the invitee accepts (no assessment data).
 * `id` is the invitation id, which is what the list links to.
 */
export type ProgramInvitationPreview = {
	_type: "invitation";
	id: string;
	programId: string;
	name: string;
	type: FinalProgramType;
	ownerName: string | null;
	isArchived: false;
	/** The program owner, matching `userId` on programs and directories */
	userId: string | null;
	createdAt: Date;
	updatedAt: Date;
};

export type ProgramDirectoryPreview = Prisma.ProgramDirectoryGetPayload<{}> & {
	_type: "directory";
};

export type ProgramDirectoryExpanded = ProgramDirectoryPreview & {
	name?: string;
	parentDirectory?: ProgramDirectoryPreview;
	/** Every program the user owns across all folders, used for the free program limit */
	ownedProgramCount: number;
	items: ProgramDirectoryItem[];
};

export type ProgramDirectoryItem =
	| ProgramPreview
	| ProgramDirectoryPreview
	| ProgramInvitationPreview;

export type LinkedProgramPreview = {
	id: string;
	name: string;
	type: FinalProgramType;
	status: ProgramStatus;
	stats: {
		phaseCount?: number;
		exerciseCount: number;
	};
};

export const updateProgramAssessmentSchema = programInputSchema;
export type UpdateProgramAssessmentFormData = z.infer<
	typeof updateProgramAssessmentSchema
>;

export const addExerciseSchema = z.discriminatedUnion("isCustom", [
	z.object({
		routineId: z.cuid(),
		isCustom: z.literal(false),
		orderIndex: z.number().nonnegative(),

		exerciseId: z.string(),
		routineType: z.enum(RoutineType).optional(),
	}),

	z.object({
		routineId: z.cuid(),
		isCustom: z.literal(true),
		orderIndex: z.number().nonnegative(),
		title: z.string().describe("i.e. Barbell Squat"),
		description: z
			.string()
			.describe("A brief description of the exercise")
			.optional(),

		routineType: z.enum(RoutineType).optional(),
	}),
]);

export const programHeaderEditSchema = z.object({
	name: z
		.string()
		.min(1, "Program name is required")
		.max(100, "Max 100 characters"),
	goal: z
		.string()
		.min(1, "Program goal is required")
		.max(500, "Max 500 characters"),
});

export type ProgramHeaderEditFormData = z.infer<typeof programHeaderEditSchema>;

export type AddExerciseFormData = z.infer<typeof addExerciseSchema>;

export const createWorkoutSchema = z.object({
	weightUnit: z.enum(["kg", "lb"]),
	notes: z.string().optional(),
	exerciseIds: z.array(z.string()).min(1),
});

export type CreateWorkoutFormData = z.infer<typeof createWorkoutSchema>;

export const endWorkoutSchema = z.object({
	endedAt: z.coerce.date(),
	notes: z.string().optional(),
});

export type EndWorkoutFormData = z.infer<typeof endWorkoutSchema>;

type WorkoutExerciseBase = Prisma.ProgramWorkoutExerciseGetPayload<{
	include: { sets: true };
}>;

export type WorkoutExerciseExpanded = WorkoutExerciseBase & {
	usesSetsAndReps: boolean;
} & (
		| {
				isCustom: false;
				exercise: Exercise;
		  }
		| {
				isCustom: true;
				customData: { title: string; description?: string };
		  }
	);

export type WorkoutExpanded = Omit<
	Prisma.ProgramWorkoutGetPayload<{}>,
	"exercises"
> & {
	exercises: WorkoutExerciseExpanded[];
};

export type WorkoutPreview = Prisma.ProgramWorkoutGetPayload<{}> & {
	durationSeconds: number | null;
	totalVolume: number;
	numberOfExercises: number;
};

export const addWorkoutExerciseSchema = z.discriminatedUnion("isCustom", [
	z.object({
		isCustom: z.literal(false),
		exerciseId: z.string(),
		laterality: z.enum(Laterality).optional(),
		notes: z.string().optional(),
	}),
	z.object({
		isCustom: z.literal(true),
		title: z.string().describe("i.e. Barbell Squat"),
		description: z.string().optional(),
		laterality: z.enum(Laterality).optional(),
		notes: z.string().optional(),
	}),
]);

export const updateWorkoutExerciseSchema = z.object({
	isCompleted: z.boolean(),
});

export type UpdateWorkoutExerciseFormData = z.infer<
	typeof updateWorkoutExerciseSchema
>;

export type AddWorkoutExerciseFormData = z.infer<
	typeof addWorkoutExerciseSchema
>;

export const addWorkoutExerciseSetSchema = z.object({
	reps: z.number().int().nonnegative().max(999),
	load: z.number().nonnegative().max(1000),
	rpe: z.number().int().min(1).max(10).optional(),

	toFailure: z.boolean(),
	isDropSet: z.boolean(),
});

export type AddWorkoutExerciseSetFormData = z.infer<
	typeof addWorkoutExerciseSetSchema
>;

export const updateWorkoutExerciseSetSchema =
	addWorkoutExerciseSetSchema.partial();

export type UpdateWorkoutExerciseSetFormData = z.infer<
	typeof updateWorkoutExerciseSetSchema
>;

export const updateWorkoutSchema = z.object({
	weightUnit: z.enum(["kg", "lb"]).optional(),
	notes: z.string().optional(),
});

export type UpdateWorkoutFormData = z.infer<typeof updateWorkoutSchema>;

export const setProgressionSchema = z.object({
	exerciseId: z.string().min(1),
	notes: z.string().optional(),
});

export type SetProgressionFormData = z.infer<typeof setProgressionSchema>;

export const programInvitationSchema = z.object({
	emailAddress: z.email("Invalid email address"),
});

export const programCreateDirectorySchema = z.object({
	name: z
		.string()
		.min(1, "Directory name is required")
		.max(100, "Max 100 characters"),
	parentDirectoryId: z.cuid().nullable().optional(),
});

export const programDirectoryUpdateNameSchema = z.object({
	name: z
		.string()
		.min(1, "Directory name is required")
		.max(100, "Max 100 characters"),
});

export type ProgramDirectoryUpdateNameFormData = z.infer<
	typeof programDirectoryUpdateNameSchema
>;

export type ProgramCreateDirectorySchema = z.infer<
	typeof programCreateDirectorySchema
>;

export const programUpdateDirectorySchema = z.object({
	directoryId: z
		.string({
			message: "Required",
		})
		.nonempty("Required"),
});

export type ProgramUpdateDirectoryFormData = z.infer<
	typeof programUpdateDirectorySchema
>;

export type ProgramInvitationFormData = z.infer<typeof programInvitationSchema>;

export interface ProgramInvitationExpanded
	extends Prisma.ProgramInvitationGetPayload<{
		include: {
			program: {
				select: {
					name: true;
					type: true;
					userId: true;
					id: true;
				};
			};
		};
	}> {}
